import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/2gCYNQnb5H3daKQHrY5crR/Lofferta_speciale_scade_tra_13_min_46_sec_Risolvi_un_Intero_Anno_di_Lezioni_di_Storia_in_soli_5_Minuti_Unisciti_a_oltre_1.800_insegnanti_che_hanno_smesso_di_improvvisare._Entra_in_classe_con_tut_(5).webp';
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sidor i huvudvolymerna'
}, {
  value: '6',
  label: 'Digitala matematikvolymer'
}, {
  value: '9',
  label: 'Årskurser – från 1 till 9'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}, {
  value: '1',
  label: 'Komplett undervisningssystem'
}];
export const differenceStats = [{
  value: 'Årskurs 1–9',
  label: 'Material organiserat i tre stadier'
}, {
  value: '6 digitala volymer',
  label: 'Från taluppfattning till algebra och statistik'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller skriva ut i klassrummet'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällarna försvinner',
  text: 'Timmar framför datorn för att planera nästa veckas lektioner – och du är fortfarande inte klar.'
}, {
  title: 'Timmar av letande',
  text: 'Övningar från olika webbplatser är för lätta, för svåra eller måste anpassas innan de går att använda.'
}, {
  title: 'Arbetsbladen skapas från grunden',
  text: 'Frågor, illustrationer, uppställning och facit – det som skulle ta minuter blir en lång arbetsuppgift.'
}, {
  title: 'Svårt att göra matematiken visuell',
  text: 'Tallinjer, bråkmodeller och geometriska figurer tar tid att ta fram för varje nytt moment.'
}, {
  title: 'Bedömning och rättning tar tid',
  text: 'Uppgifter, lösningar och uppföljning av elevernas utveckling blir ännu mer arbete efter skoltid.'
}, {
  title: 'Stress inför nästa lektion',
  text: 'När tiden inte räcker till blir det lätt att improvisera och få ihop material i sista minuten.'
}];
export const afterItems = ['Ett organiserat matematikpaket för hela grundskolan', 'Lektionsupplägg som är enkla att använda', 'Färdiga matematikuppgifter', 'Arbetsblad som kan skrivas ut direkt', 'Visuella förklaringar och matematiska modeller', 'Aktiviteter för repetition och färdighetsträning', 'Matematikspel som skapar variation', 'Bedömningsmaterial och facit', 'Planeringsstöd för hela läsåret'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Söndagarna går åt till planering',
  text: 'Lägger du delar av helgen på att skapa matematiklektioner inför kommande vecka?'
}, {
  icon: FolderSearchIcon,
  title: 'Du vet inte vilket arbetsblad du ska använda',
  text: 'Letar du ständigt efter övningar som passar elevernas nivå?'
}, {
  icon: LightbulbIcon,
  title: 'Du vill ha tydligare förklaringar',
  text: 'Behöver du mer visuellt material för att förklara matematiska begrepp på ett enkelt sätt?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektioner, uppgifter och aktiviteter redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna behöver mer variation',
  text: 'Vill du använda fler spel, utmaningar och praktiska aktiviteter i undervisningen?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha materialet samlat så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Åk 1–3',
  title: 'Tal & grundläggande räkning',
  tagline: 'Ge eleverna en trygg grund i matematiken.',
  intro: 'Den första volymen fokuserar på grundläggande taluppfattning och de fyra räknesätten.',
  items: ['Tal och talföljder', 'Addition och subtraktion', 'Multiplikation och division', 'Tiokamrater och tallinjer', 'Visuella räkneövningar', 'Problemlösning', 'Utskrivbara arbetsblad', 'Matematikspel', 'Repetition och bedömning'],
  outro: 'Färdigt material som gör grundläggande matematik konkret och begriplig.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Åk 1–3',
  title: 'Geometri, mätning & problemlösning',
  tagline: 'Hjälp eleverna att upptäcka matematiken i världen omkring dem.',
  intro: 'Den andra volymen innehåller visuella och praktiska aktiviteter inom geometri, mätning och problemlösning.',
  items: ['Geometriska figurer', 'Tvådimensionella och tredimensionella former', 'Symmetri och mönster', 'Längd och måttenheter', 'Klockan och tid', 'Textuppgifter från vardagen', 'Praktiska matematikaktiviteter', 'Visuella arbetsblad', 'Repetition och bedömning'],
  outro: 'Färdiga övningar där eleverna får undersöka, rita, mäta och lösa problem.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Åk 4–6',
  title: 'Tal, bråk & procent',
  tagline: 'Gör bråk, decimaltal och procent enklare att förstå.',
  intro: 'Den tredje volymen hjälper eleverna att utveckla sin taluppfattning och se sambanden mellan olika matematiska representationer.',
  items: ['Naturliga tal', 'Positionssystemet', 'Decimaltal', 'Bråk och bråkräkning', 'Procent', 'Proportionalitet', 'Visuella bråkmodeller', 'Problemlösning', 'Utskrivbara arbetsblad', 'Bedömningar och facit'],
  outro: 'Tydliga exempel och aktiviteter för matematikundervisningen i årskurs 4–6.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Åk 4–6',
  title: 'Algebra, geometri & statistik',
  tagline: 'Utveckla elevernas matematiska resonemang.',
  intro: 'Den fjärde volymen kombinerar algebra, geometri och statistik med visuella uppgifter och praktiska aktiviteter.',
  items: ['Algebraiska uttryck', 'Enkla ekvationer', 'Tal- och figurmönster', 'Geometriska figurer', 'Area och omkrets', 'Volym', 'Tabeller och diagram', 'Statistik och sannolikhet', 'Matematikspel', 'Bedömningsuppgifter'],
  outro: 'Ett praktiskt stöd för att förklara mer avancerade matematiska samband.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Åk 7–9',
  title: 'Algebra, funktioner & samband',
  tagline: 'Hjälp eleverna att förstå algebra och funktioner steg för steg.',
  intro: 'Den femte volymen innehåller färdiga aktiviteter för att arbeta med ekvationer, koordinatsystem och matematiska samband.',
  items: ['Algebra och variabler', 'Algebraiska uttryck', 'Ekvationer', 'Koordinatsystem', 'Linjära funktioner', 'Grafer och tabeller', 'Proportionalitet', 'Matematiska modeller', 'Problemlösning', 'Repetition och bedömning'],
  outro: 'Visuella och strukturerade resurser för matematikundervisningen på högstadiet.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Åk 7–9',
  title: 'Geometri, statistik & nationella prov',
  tagline: 'Låt eleverna fördjupa sina kunskaper och träna matematiskt resonemang.',
  intro: 'Den sjätte volymen innehåller geometri, statistik, sannolikhet och egenproducerade övningar som stöd inför de nationella proven.',
  items: ['Geometriska figurer och vinklar', 'Area och volym', 'Pythagoras sats', 'Statistik', 'Sannolikhet', 'Diagram och tabeller', 'Matematiska resonemang', 'Problemlösningsuppgifter', 'Provliknande övningar', 'Facit och bedömningsstöd'],
  outro: 'Ett komplett komplement för repetition, problemlösning och fördjupning.',
  note: 'Övningarna är självständigt framtagna och är inte officiella provuppgifter från Skolverket.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitala volymer för årskurs 1–9',
  text: 'Ett komplett matematikpaket som hjälper dig att planera och genomföra undervisningen under hela läsåret. Varje volym innehåller 40 sidor med matematikmaterial, övningar och klassrumsaktiviteter.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Färdiga lektioner',
  text: 'Slipp börja från ett tomt dokument. Tydliga lektionsupplägg, exempel och aktiviteter hjälper dig att komma igång snabbare.'
}, {
  icon: FileTextIcon,
  title: 'Utskrivbara arbetsblad',
  text: 'Matematikuppgifter som kan skrivas ut och användas direkt – för självständigt arbete, repetition och extra träning.'
}, {
  icon: ShapesIcon,
  title: 'Visuella matematikövningar',
  text: 'Tallinjer, geometriska figurer, bråkmodeller och diagram gör matematiken mer konkret och lättare att förstå.'
}, {
  icon: DicesIcon,
  title: 'Matematikspel och aktiviteter',
  text: 'Spel, utmaningar och praktiska övningar ger eleverna fler sätt att träna och använda sina kunskaper.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömningar och facit',
  text: 'Färdiga bedömningsuppgifter och lösningar som stöd för rättning och uppföljning.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplanering',
  text: 'Stöd för att organisera matematikundervisningen och välja arbetsområden utifrån din planering.'
}];
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/9acf5810-3b9e-4362-903b-ee95e79caa40.jpg',
  label: 'Arbetsblad 12',
  title: 'Subtraktion med tvåsiffriga tal',
  text: 'Träna subtraktion med och utan växling – med tydliga exempel, positionssystemet och visuella förklaringar.'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/b1fe249f-62e7-4a40-b5f8-4b49d1e84c37.jpg',
  label: 'Arbetsblad 17',
  title: 'Multiplikation och division',
  text: 'Sambandet mellan multiplikation och division med talfamiljer, beräkningar och problemlösning med visuellt stöd.'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/ebc72a83-34bc-40db-ae6e-d5bf630e5c70.jpg',
  label: 'Arbetsblad 14',
  title: 'Textuppgifter i vardagen',
  text: 'Eleverna läser, förstår, räknar och skriver sina svar direkt på arbetsbladet.'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/cb185705-efe2-4f9f-a968-4e063e11ff1b.jpg',
  label: 'Arbetsblad 15',
  title: 'Geometri på rutat papper',
  text: 'Geometriska figurer, symmetri, area och omkrets – eleverna ritar, undersöker och löser uppgifter.'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/713d9fac-738e-4964-9b30-cc79559c5c60.jpg',
  label: 'Matematikkoll',
  title: 'Årskurs 2',
  text: 'Ett strukturerat bedömningsblad med plats för elevsvar och lärarens observationer.'
}];
export const pageTypes = ['Färdiga lektioner', 'Utskrivbara arbetsblad', 'Praktiska aktiviteter', 'Bedömningar och facit'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Årsplanering i matematik',
  text: 'Organisera arbetsområden, aktiviteter och lektionsmål för årskurs 1–9 och få bättre struktur under hela läsåret.',
  items: ['Stöd för årsplanering', 'Matematiska arbetsområden', 'Lektionsmål och aktiviteter', 'Praktiska planeringsmallar'],
  benefit: 'Mindre tid på planering och mer tid till undervisning.'
}, {
  title: '100 extra arbetsblad',
  text: 'En extra samling utskrivbara arbetsblad för olika årskurser och matematiska områden.',
  items: ['Extra matematikövningar', 'Repetitionsuppgifter', 'Färdighetsträning', 'Uppgifter på olika nivåer'],
  benefit: 'Perfekt när du snabbt behöver fler övningar.'
}, {
  title: 'Matematikspel & aktivitetskort',
  text: 'Spel och aktivitetskort som låter eleverna träna matematik på ett engagerande sätt.',
  items: ['Utskrivbara spel', 'Matematiska utmaningar', 'Aktivitetskort', 'Övningar i par'],
  benefit: 'Mer variation i klassrummet utan att du behöver skapa spelen själv.'
}, {
  title: 'Bedömningspaket & facit',
  text: 'Kompletterande material för att följa elevernas förståelse och se vilka områden som behöver tränas mer.',
  items: ['Bedömningsuppgifter', 'Diagnostiska övningar', 'Underlag för uppföljning', 'Facit och lösningar'],
  benefit: 'Ett praktiskt stöd för bedömningsarbetet.'
}, {
  title: 'Lärarhandledning & lektionsplaner',
  text: 'Lektionsupplägg och pedagogiska resurser som hjälper dig att använda materialet i undervisningen.',
  items: ['Lektionsplaner', 'Pedagogiska instruktioner', 'Förslag på klassrumsaktiviteter', 'Anpassningar för olika behov'],
  benefit: 'Känn dig bättre förberedd inför varje matematiklektion.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna MATTE KLAR™',
  text: 'Ladda ner paketet och öppna volymen som passar din årskurs.'
}, {
  title: 'Välj lektion och material',
  text: 'Hitta rätt arbetsområde och välj de övningar, arbetsblad eller aktiviteter som passar dina elever.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom instruktionerna och välj det material du behöver.'
}, {
  title: 'Skriv ut och undervisa',
  text: 'Skriv ut arbetsbladen eller använd materialet digitalt – och gå in i klassrummet med en tydlig plan.'
}];
export const audienceItems = ['Undervisar i matematik i årskurs 1–3, 4–6 eller 7–9', 'Vill ha ett helt läsårs matematikmaterial organiserat', 'Vill slippa skapa varje arbetsblad från grunden', 'Behöver fler visuella matematikövningar', 'Vill använda matematikspel och praktiska aktiviteter', 'Vill ha enkel tillgång till bedömningsmaterial och facit', 'Vill spara tid på veckoplaneringen', 'Vill känna sig bättre förberedd inför undervisningen'];
export const timeValueTasks = ['Leta efter arbetsblad', 'Skriva övningar', 'Skapa visuella förklaringar', 'Förbereda aktiviteter', 'Rätta och följa upp elevernas arbete'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Tal, geometri, bråk, algebra, statistik och problemlösning'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Årsplanering, 100 extra arbetsblad, spel, bedömningar och lektionsplaner'
}, {
  label: 'För årskurs 1–9',
  detail: 'Färdiga lektioner, arbetsblad och facit'
}];
export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara arbetsblad', 'Ingen fysisk produkt skickas'];
export const PRICE = '299\u00A0kr';

// Only add authentic reviews of MATTE KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'MATTE KLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de arbetsblad du behöver.'
}, {
  q: 'Är MATTE KLAR™ anpassat för den svenska skolan?',
  a: 'Ja. Materialet är framtaget för matematikundervisning i den svenska grundskolan, med utgångspunkt i det centrala innehållet i Lgr22. Det är ett fristående undervisningsmaterial och inte en officiell produkt från Skolverket.'
}, {
  q: 'Passar det för årskurs 1–9?',
  a: 'Ja. De sex volymerna är organiserade i tre stadier – årskurs 1–3, 4–6 och 7–9. Du väljer material utifrån den årskurs och det matematiska område du undervisar i.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'MATTE KLAR™ är utformat för att göra lektionsförberedelserna snabbare. Du kan öppna en volym, välja ett färdigt arbetsblad eller lektionsupplägg och gå igenom det på ungefär fem minuter. Tiden kan variera beroende på lektionsinnehåll, elevgrupp och behov av anpassning.'
}, {
  q: 'Räcker materialet för ett helt läsår?',
  a: 'MATTE KLAR™ innehåller material och aktiviteter som kan användas under hela läsåret. Du väljer, kombinerar och anpassar resurserna efter din årsplanering och elevernas behov.'
}, {
  q: 'Finns det färdiga arbetsblad?',
  a: 'Ja. Materialet innehåller utskrivbara arbetsblad med uppgifter, övningar och visuella förklaringar.'
}, {
  q: 'Ingår facit?',
  a: 'Ja. Paketet innehåller facit och lösningar som stöd för rättning och genomgång.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. MATTE KLAR™ innehåller varierade uppgifter och aktiviteter som du kan välja och anpassa utifrån elevernas kunskaper och behov.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Du väljer själv vilka sidor du skriver ut, och du kan även använda delar av materialet digitalt.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner och spara PDF-filerna för fortsatt användning enligt produktens användarvillkor.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil. För att granska layouten på arbetsbladen före utskrift rekommenderar vi dator eller surfplatta.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. MATTE KLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom garantiperioden enligt villkoren.'
}];
export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
